import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import './AddMemberPage.css';
import vkslogo from './vkslogo.png';

interface UserProfile {
  id: number;
  username: string;
  name: string;
  role: string;
  mobile: string | null;
  city: string | null;
  wallet_balance: number;
  sponsor_id?: string | null;
  sponsor_name?: string | null;
}

const AddMemberPage: React.FC = () => {
  const { user } = useAuth();
  const [memberName, setMemberName] = useState('');
  const [mobile, setMobile] = useState('');
  const [city, setCity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formSponsorId, setFormSponsorId] = useState('');
  const [newMemberCredentials, setNewMemberCredentials] = useState<{ memberId: string; defaultPassword: string; memberName: string; joiningDate: string; } | null>(null);
  const [payerUsername, setPayerUsername] = useState('');
  const [payerVerification, setPayerVerification] = useState<{ status: 'success' | 'error'; message: string } | null>(null);
  const [manualIdEntry, setManualIdEntry] = useState(false);
  const [manualMemberId, setManualMemberId] = useState('');

  useEffect(() => {
    if (user) {
      setFormSponsorId(user.username);
    }
  }, [user]);

  const handleVerifyPayer = async () => {
    if (!payerUsername) {
      setPayerVerification({ status: 'error', message: 'Please enter a username to verify.' });
      return;
    }
    try {
      // We can reuse the /api/profile endpoint which already has the logic
      const response = await fetch(`/api/profile?username=${encodeURIComponent(payerUsername)}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'User not found.');
      }
      setPayerVerification({ status: 'success', message: `Payer Found: ${data.name} (${data.username})` });

      // When an admin verifies a payer, the payer becomes the sponsor.
      setFormSponsorId(data.username);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setPayerVerification({ status: 'error', message: errorMessage });
    }
  };

  const handleClearPayerSearch = () => {
    setPayerUsername('');
    setPayerVerification(null);
    // If the payer username is cleared, revert sponsor to the logged-in user
    if (user) {
      setFormSponsorId(user.username);
    }
  };


  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setNewMemberCredentials(null);

    if (!formSponsorId) {
        setError('Sponsor ID is required.');
        setIsLoading(false);
        return;
    }

    // Client-side validation mirroring member.routes.ts
    if (memberName.length > 50) {
      setError("Member Name cannot be longer than 50 characters.");
      setIsLoading(false);
      return;
    }
    if (city.length > 30) {
      setError("City cannot be longer than 30 characters.");
      setIsLoading(false);
      return;
    }
    if (!/^\d{10}$/.test(mobile)) {
      setError('Mobile number must be 10 digits.');
      setIsLoading(false);
      return;
    }

    const requestBody: any = {
      memberName,
      mobile,
      city,
      sponsorId: formSponsorId,
    };

    if (user?.role === 'admin' && payerUsername) {
      requestBody.payerUsername = payerUsername;
    }

    if (manualIdEntry && manualMemberId) {
      requestBody.memberId = manualMemberId; // Use manual member ID
    }

    try {
      const response = await fetch('/api/members/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important for sending auth cookie
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to add member.');
      }

      setSuccess(data.message);
      setNewMemberCredentials({ memberId: data.memberid, defaultPassword: data.defaultPassword, memberName: data.memberName, joiningDate: data.joiningDate });
      window.alert(data.message); // Show success message in a popup
      // Clear form on success
      setMemberName('');
      setMobile('');
      setCity('');
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generateAndDownloadPdf = async (memberId: string, password: string, memberName: string, joiningDate: string) => {
    try {
      const response = await fetch('/api/members/welcome-letter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          memberId,
          memberName,
          password,
          joiningDate,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to generate welcome letter.' }));
        throw new Error(errorData.message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `welcome_${memberId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred.';
      setError(errorMessage);
    }
  };

  return (
    <div className="add-member-container">
      <style>{`
        .input-with-button {
          display: flex;
          align-items: center;
        }
        .input-with-button .search-input-wrapper {
          flex-grow: 1;
        }
        .form-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
        }
        @media (max-width: 768px) {
          .add-member-layout { flex-direction: column; }
          .input-with-button, .form-actions {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
      <div className="add-member-layout">
        <div className="add-member-form-card">
          <form className="add-member-form" onSubmit={handleSubmit}>
            <h2>Add New Member</h2>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            
            {user?.role === 'admin' && (
              <div className="form-group payer-verification-group">
                <label htmlFor="payerUsername">Payer Username (Admin)</label>
                <div className="input-with-button">
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      id="payerUsername"
                      value={payerUsername}
                      onChange={(e) => {
                        setPayerUsername(e.target.value);
                        setPayerVerification(null); // Reset on change
                      }}
                      placeholder="Enter username of the paying member"
                    />
                    {payerUsername && (
                      <button type="button" onClick={handleClearPayerSearch} className="clear-search-button">&times;</button>
                    )}
                  </div>
                  <button type="button" onClick={handleVerifyPayer} className="verify-button">Verify</button>
                </div>
                {payerVerification && (
                  <div className={`verification-message ${payerVerification.status}`}>
                    {payerVerification.message}
                  </div>
                )}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="sponsorId">Sponsor ID</label>
              <input type="text" id="sponsorId" value={formSponsorId} required disabled />
            </div>
            {/* Manual ID Entry Toggle & Input (Conditionally Rendered for Admins) */}
            {user?.role === 'admin' && (
              <div className="form-group manual-id-section">
                <label className="manual-id-label">
                  <input type="checkbox" checked={manualIdEntry} onChange={(e) => setManualIdEntry(e.target.checked)} />
                  <span>Allow Manual Member ID Entry?</span>
                </label>
                {manualIdEntry && (
                  <input
                    type="text"
                    id="manualMemberId"
                    value={manualMemberId}
                    onChange={(e) => setManualMemberId(e.target.value)}
                    required={manualIdEntry} // Only required when manual entry is enabled
                    disabled={isLoading}
                    placeholder="Enter manual member ID"
                    className="manual-id-input"
                  />
                )}
              </div>
            )}




            <div className="form-group">
              <label htmlFor="memberName">Member Full Name</label>
              <input type="text" id="memberName" value={memberName} onChange={(e) => setMemberName(e.target.value)} required disabled={isLoading} maxLength={50} />
            </div>

            <div className="form-group">
              <label htmlFor="city">City</label>
              <input type="text" id="city" value={city} onChange={(e) => setCity(e.target.value)} required disabled={isLoading} maxLength={30} />
            </div>

            <div className="form-group">
              <label htmlFor="mobile">Mobile</label>
              <input
                type="tel"
                id="mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
                disabled={isLoading}
                pattern="\d{10}"
                maxLength={10}
                title="Mobile number must be 10 digits."
              />
            </div>

            <div className="form-actions">
                <button type="submit" className="add-member-button" disabled={isLoading}>
                    {isLoading ? 'Adding Member...' : 'Add Member'}
                </button>
                {success && newMemberCredentials && (
                    <button
                      type="button"
                      className="add-member-button"
                      onClick={() => newMemberCredentials && generateAndDownloadPdf(newMemberCredentials.memberId, newMemberCredentials.defaultPassword, newMemberCredentials.memberName, newMemberCredentials.joiningDate)}
                      disabled={!newMemberCredentials}
                    >Download Welcome Letter</button>
                )}
            </div>
          </form>
        </div>
        <div className="info-card">
            <h3>Registration Guidelines</h3>
            <p>Please review these details before adding a new member to your network.</p>
            <ul>
              <li><strong>New Member:</strong> Creates a full, loginable user account with an auto-generated ID (e.g., VKS123456). Inform them of their credentials.</li>
            </ul>
        </div>
      </div>
    </div>
  );
};

export default AddMemberPage;